import type { MfaStep } from './signIn.types';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import OtpInput from 'react-otp-input';
import { getEnvConfig } from '@configs/env';
import { ProviderIcon } from '@configs/providerIcons';
import styled from '@emotion/styled';
import { Button } from '@mui/material';
import { MFASettingModal } from '@pages/customer/employee/gnb/myProfileModal/MFASettingModal';
import { fonts } from '@theme/fontsCustomer';

interface MfaVerificationProps {
  /** OTP 검증 핸들러 */
  onVerify: (otpCode: string) => void;
  /** 뒤로가기 (로그인 화면으로) */
  onBack: () => void;
  /** 로딩 상태 */
  isLoading?: boolean;
  /** 에러 메시지 */
  error?: string | null;
  /** MFA 단계 변경 핸들러 */
  onChangeMfaStep?: (step: MfaStep) => void;
}

export const MfaVerification = ({
  onVerify,
  onBack,
  isLoading = false,
  error,
  onChangeMfaStep,
}: MfaVerificationProps) => {
  const { t } = useTranslation();
  const { COMPANY_ID } = getEnvConfig();
  const [otp, setOtp] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isMfaResetModalOpen, setIsMfaResetModalOpen] = useState(false);

  // 에러 발생 시 OTP 초기화 및 제출 상태 리셋
  useEffect(() => {
    if (error) {
      setOtp('');
      setHasSubmitted(false);
    }
  }, [error]);

  // OTP 6자리 입력 완료 시 자동 제출 (한 번만 )
  useEffect(() => {
    if (otp.length === 6 && !isLoading && !error && !hasSubmitted) {
      setHasSubmitted(true);
      onVerify(otp);
    }
  }, [otp, isLoading, error, hasSubmitted, onVerify]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length === 6) {
      onVerify(otp);
    }
  };

  const handleChangeOtp = (otp: string) => {
    setOtp(otp);
  };

  const handleTogggleMfaResetModal = (bool: boolean) => {
    setIsMfaResetModalOpen(bool);
  };

  const loginIconWidth = COMPANY_ID === 'ZENITH' ? 181 : 141;

  return (
    <Container>
      <Content>
        <Header>
          <Logo>{ProviderIcon({ providerName: COMPANY_ID, variant: 'login', width: loginIconWidth, height: 25 })}</Logo>
          <Title>{t('Mfa.verification.title')}</Title>
          <Description>{t('Mfa.verification.description')}</Description>
        </Header>

        <Form onSubmit={handleSubmit}>
          <OtpSection>
            <OtpInputWrapper>
              <OtpInput
                value={otp}
                onChange={handleChangeOtp}
                numInputs={6}
                shouldAutoFocus
                inputType="number"
                renderInput={props => <OtpInputField {...props} hasError={!!error} />}
              />
            </OtpInputWrapper>
            {error && <ErrorText>{error}</ErrorText>}
          </OtpSection>

          <SubmitButton
            type="submit"
            size="large"
            variant="contained"
            color="primary"
            fullWidth
            disabled={otp.length !== 6 || isLoading}
          >
            {isLoading ? t('Common.loading') : t('Mfa.verification.confirm')}
          </SubmitButton>
        </Form>

        <BackButton type="button" onClick={onBack}>
          {t('Mfa.verification.backToLogin')}
        </BackButton>

        <ResetLinkWrapper>
          <ResetLink type="button" onClick={() => handleTogggleMfaResetModal(true)}>
            {t('Mfa.verification.resetLink')}
          </ResetLink>
        </ResetLinkWrapper>
      </Content>
      {isMfaResetModalOpen && (
        <MFASettingModal
          open={isMfaResetModalOpen}
          onClose={() => handleTogggleMfaResetModal(false)}
          onChangeMfaStep={onChangeMfaStep}
          mfaEnabled
          mfaStep="DISABLE_CONFIRM"
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: calc(100vh - var(--employee-gnb-height));
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors['bg-gray-light']};
`;

const Content = styled.div`
  width: 488px;
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 36px 48px 40px;
  box-sizing: border-box;
  border-radius: 8px;
  box-shadow: 0px 1px 6px 0px rgba(0, 0, 0, 0.06);
  background-color: ${({ theme }) => theme.colors['bg-white']};
`;

const Header = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 32px;
`;

const Logo = styled.div`
  width: 100%;
  display: flex;
  align-self: flex-start;
  margin-bottom: 48px;
`;

const Title = styled.h1`
  ${fonts.Headline3}
  margin: 0 0 12px 0;
  text-align: center;
`;

const Description = styled.p`
  ${fonts.Body2}
  color: ${({ theme }) => theme.colors['text-gray-sub-dark']};
  margin: 0;
  text-align: center;
  line-height: 1.5;
  white-space: pre-line;
`;

const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const OtpSection = styled.div`
  width: 100%;
`;

const OtpInputWrapper = styled.div`
  display: flex;
  justify-content: center;

  & > div {
    gap: 8px;
  }
`;

const OtpInputField = styled.input<{ hasError?: boolean }>`
  width: 52px !important;
  height: 64px;
  ${fonts.Headline2}
  text-align: center;
  border: 1px solid
    ${({ hasError, theme }) => (hasError ? theme.colors['border-red'] : theme.colors['border-gray-light'])};
  border-radius: 4px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    border-color: ${({ hasError, theme }) => (hasError ? theme.colors['border-red'] : theme.colors['border-primary'])};
    box-shadow: 0 0 0 2px
      ${({ hasError, theme }) => (hasError ? 'rgba(255, 0, 0, 0.1)' : theme.colors['bg-primary-light'])};
  }

  /* 숫자 입력 화살표 제거 */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
  appearance: textfield;
`;

const ErrorText = styled.span`
  display: block;
  margin-top: 12px;
  color: ${({ theme }) => theme.colors['text-red-light']};
  ${fonts.Body3};
  text-align: center;
`;

const SubmitButton = styled(Button)`
  box-shadow: none;
  background-color: ${({ theme }) => theme.colors['bg-primary']};
  &:hover {
    box-shadow: none;
  }
  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors['bg-primary-dark']};
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const BackButton = styled.button`
  ${fonts.Body3}
  margin-top: 24px;
  padding: 8px 16px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors['text-gray-sub-dark']};
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: ${({ theme }) => theme.colors['text-gray-main']};
  }
`;

const ResetLinkWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const ResetLink = styled.button`
  ${fonts.Body3}
  padding: 4px 8px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors['text-gray-sub-dark']};
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: ${({ theme }) => theme.colors['text-gray-main']};
  }
`;
